import { useMemo } from 'react';
import { IncidentSeverity } from 'types';

/** Default SLA thresholds in minutes per severity. */
const DEFAULT_ACK_SLA: Record<IncidentSeverity, number> = {
  [IncidentSeverity.High]: 15,
  [IncidentSeverity.Medium]: 30,
  [IncidentSeverity.Low]: 60,
  [IncidentSeverity.Informational]: 120,
};

export type SlaStatus = 'ok' | 'warning' | 'breached';

export interface SlaInfo {
  remainingMs: number;
  remainingFormatted: string;
  status: SlaStatus;
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return '00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function calculateSla(
  slaDeadline: string,
  severity: IncidentSeverity,
  now: Date = new Date(),
): SlaInfo {
  const deadline = new Date(slaDeadline);
  const remainingMs = deadline.getTime() - now.getTime();
  const ackMinutes = DEFAULT_ACK_SLA[severity];
  const warningThresholdMs = ackMinutes * 60 * 1000 * 0.25; // warn at 25% remaining

  let status: SlaStatus = 'ok';
  if (remainingMs <= 0) {
    status = 'breached';
  } else if (remainingMs <= warningThresholdMs) {
    status = 'warning';
  }

  return {
    remainingMs,
    remainingFormatted: formatRemaining(remainingMs),
    status,
  };
}

export function useSlaCalculation(
  slaDeadline: string | undefined,
  severity: IncidentSeverity,
  now: Date,
): SlaInfo | null {
  return useMemo(() => {
    if (!slaDeadline) return null;
    return calculateSla(slaDeadline, severity, now);
  }, [slaDeadline, severity, now]);
}
